import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ElementIcon = ({ element, color }: { element: string; color: string }) => {
  const icons = {
    fire: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8 0-3.31 2.69-6 6-6s6 2.69 6 6c0 4.41-3.59 8-8 8z"
          fill="currentColor"
        />
        <path
          d="M12.5 7.5c-.76-.76-1.86-1.11-2.83-.9-.95.2-1.73.98-1.93 1.93-.2.97.14 2.07.9 2.83L12 14.72l3.36-3.36c.76-.76 1.11-1.86.9-2.83-.2-.95-.98-1.73-1.93-1.93-.97-.2-2.07.14-2.83.9z"
          fill="currentColor"
        />
      </svg>
    ),
    water: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path
          d="M12 2L5 12c0 3.87 3.13 7 7 7s7-3.13 7-7l-7-10zM12 17c-2.76 0-5-2.24-5-5 0-1.94 1.11-3.62 2.72-4.44C10.55 7.19 11.26 7 12 7c.74 0 1.45.19 2.28.56C15.89 8.38 17 10.06 17 12c0 2.76-2.24 5-5 5z"
          fill="currentColor"
        />
      </svg>
    ),
    earth: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
          fill="currentColor"
        />
      </svg>
    ),
    air: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path
          d="M14.5 17c0 1.65-1.35 3-3 3s-3-1.35-3-3h2c0 .55.45 1 1 1s1-.45 1-1-.45-1-1-1H2v-2h9.5c1.65 0 3 1.35 3 3zM19 6.5C19 4.57 17.43 3 15.5 3S12 4.57 12 6.5h2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S17.33 8 16.5 8H2v2h14.5c1.93 0 3.5-1.57 3.5-3.5s-1.57-3.5-3.5-3.5z"
          fill="currentColor"
        />
      </svg>
    ),
  };

  return (
    <div className={`text-${color.split('-')[1]}-500`}>
      {icons[element as keyof typeof icons]}
    </div>
  );
};

export default function Hero() {
  const [currentElement, setCurrentElement] = useState(0);
  
  const elements = [
    {
      name: "The Flames of Rebirth",
      element: "fire",
      description: "Masters of destruction and rebirth, wielding the primal force of flame to overwhelm their opponents.",
      bgColor: "bg-red-900/95",
      bgEffect: "after:absolute after:inset-0 after:bg-[radial-gradient(circle_800px_at_50%_-30%,#ff4d4d,transparent)] after:animate-pulse",
      character: "/rumble 1/9773.png",
      color: "from-red-500",
      ability: "Phoenix Rising: Unleash a devastating flame burst, dealing massive damage and gaining temporary invulnerability."
    },
    {
      name: "The Eternal Tides",
      element: "water",
      description: "Fluid and adaptable warriors who control the flow of battle with precision and grace.",
      bgColor: "bg-blue-900/95",
      bgEffect: "after:absolute after:inset-0 after:bg-[linear-gradient(45deg,transparent_25%,rgba(59,130,246,0.2)_50%,transparent_75%)] after:bg-[length:200%_200%] after:animate-water-flow",
      character: "/rumble 1/9764.png",
      color: "from-blue-500",
      ability: "Tidal Wave: Create a massive surge that pushes enemies back and heals allies in its path."
    },
    {
      name: "The Guardians of the Land",
      element: "earth",
      description: "Steadfast protectors who shape the battlefield and outlast their opponents.",
      bgColor: "bg-emerald-900/95",
      bgEffect: "after:absolute after:inset-0 after:bg-[repeating-linear-gradient(45deg,transparent,transparent_50px,rgba(16,185,129,0.1)_50px,rgba(16,185,129,0.1)_100px)]",
      character: "/rumble 1/9834.png",
      color: "from-emerald-600",
      ability: "Earthen Shield: Form an impenetrable barrier that absorbs damage and can be shattered to stun nearby enemies."
    },
    {
      name: "The Whispering Winds",
      element: "air",
      description: "Swift and elusive fighters who control the battlefield from above.",
      bgColor: "bg-cyan-900/95",
      bgEffect: "after:absolute after:inset-0 after:bg-[radial-gradient(circle_at_center,transparent_0%,#22d3ee_120%)] after:opacity-5 after:animate-wind-swirl",
      character: "/rumble 1/9733.png",
      color: "from-cyan-500",
      ability: "Cyclone Strike: Create a powerful whirlwind that lifts enemies and deals continuous damage."
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentElement((prev) => (prev + 1) % elements.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden pt-16">
      {/* Background Color with Effects */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentElement}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className={`absolute inset-0 ${elements[currentElement].bgColor} ${elements[currentElement].bgEffect} relative`}
        >
          {/* Additional Element-specific Effects */}
          {elements[currentElement].element === 'fire' && (
            <div className="absolute inset-0 bg-[radial-gradient(circle_400px_at_50%_50%,rgba(255,69,0,0.2),transparent)] animate-fire-pulse" />
          )}
          {elements[currentElement].element === 'water' && (
            <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(0,0,255,0.1)_0%,transparent_100%)] animate-wave" />
          )}
          {elements[currentElement].element === 'earth' && (
            <div className="absolute inset-0 bg-[conic-gradient(from_0deg_at_50%_50%,rgba(16,185,129,0.1)_0deg,transparent_60deg,rgba(16,185,129,0.1)_120deg,transparent_180deg,rgba(16,185,129,0.1)_240deg,transparent_300deg,rgba(16,185,129,0.1)_360deg)]" />
          )}
          {elements[currentElement].element === 'air' && (
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,211,238,0.1),transparent_70%)] animate-wind-pulse" />
          )}
        </motion.div>
      </AnimatePresence>
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/25 z-[1]" />

      {/* Element Indicators */}
      <div className="absolute right-8 top-1/2 transform -translate-y-1/2 z-20 flex flex-col gap-4">
        {elements.map((element, index) => (
          <button
            key={element.element}
            onClick={() => setCurrentElement(index)}
            className={`w-12 h-12 rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
              currentElement === index 
                ? `border-${element.color.split('-')[1]}-500 bg-${element.color.split('-')[1]}-500/20` 
                : 'border-white/50 hover:border-white'
            }`}
          >
            <ElementIcon element={element.element} color={element.color} />
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 h-[calc(100vh-4rem)]">
        <div className="h-full flex flex-col-reverse md:flex-row items-center justify-between gap-8 py-10 md:py-0">
          {/* Left Side - Text Content */}
          <div className="flex-1 max-w-xl text-center md:text-left">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentElement}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="hero-title mb-4 md:mb-8 text-5xl md:text-7xl code-mono">
                  RUMBLE
                </h1>
                <h2 className={`text-2xl mb-4 font-bold bg-gradient-to-r ${elements[currentElement].color} to-white bg-clip-text text-transparent code-mono`}>
                  {elements[currentElement].name}
                </h2>
                <p className="text-lg md:text-xl mb-8 gaming-text px-4 md:px-0 code-mono">
                  {elements[currentElement].description}
                </p>
                <div className="bg-black/30 p-4 rounded-lg mb-8">
                  <h3 className="text-lg font-bold mb-2 code-mono">Signature Ability</h3>
                  <p className="text-sm code-mono">{elements[currentElement].ability}</p>
                </div>
                <button className="button-gaming animate-gaming-glow text-lg md:text-xl px-8 md:px-12 py-3 md:py-4 w-full md:w-auto code-mono">
                  Join The Battle
                </button>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right Side - Character Image */}
          <div className="flex-1 relative w-full md:w-auto px-4 md:px-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentElement}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="relative w-full max-w-[300px] md:max-w-[600px] mx-auto"
              >
                <img
                  src={elements[currentElement].character}
                  alt={elements[currentElement].name}
                  className="relative w-full h-auto"
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
